import { SupplierUserRole } from '@choco-admin/types';
import { Autocomplete, AutocompleteItem, Input } from '@heroui/react';
import React, { useState } from 'react';

import { BaseModal } from '../../../../components/BaseModal';
import { SupplierRoleSelect } from '../../../../components/SupplierRoleSelect';
import { useNotification } from '../../../../contexts/NotificationContext';
import {
  SUPPLIER_SEARCH_MIN_CHARS,
  useSsoRoleMappingCreate,
  useSsoSupplierSearch,
} from '../../hooks';
import type { SsoWriteFailure } from '../../types';
import { failureMessage, reasonForField, shortenId } from '../../utils';

export interface CreateRoleMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionId: string;
  connectionName: string;
  /** The roles this connection already maps. One mapping per role. */
  existingRoles: string[];
}

const SEARCH_HINT = `Type at least ${SUPPLIER_SEARCH_MIN_CHARS} characters to search`;

export const CreateRoleMappingModal: React.FC<CreateRoleMappingModalProps> = ({
  isOpen,
  onClose,
  connectionId,
  connectionName,
  existingRoles,
}) => {
  const { addNotification } = useNotification();
  const { suppliers, isSearching, searchSuppliers } = useSsoSupplierSearch();
  const { createRoleMapping, isCreating } = useSsoRoleMappingCreate();

  const [federatedRole, setFederatedRole] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [chocoSupplierRole, setChocoSupplierRole] = useState<SupplierUserRole>(
    SupplierUserRole.SALES_REP
  );
  const [failure, setFailure] = useState<SsoWriteFailure | null>(null);

  const trimmedRole = federatedRole.trim();

  /**
   * Blank space around the role is refused rather than trimmed away. The
   * backend trims too, so a padded value silently becomes the trimmed one and
   * collides with a mapping that is already there — reported as a role that
   * already exists, naming a role the person cannot see on their own screen.
   */
  const whitespaceReason = (() => {
    if (federatedRole.length === 0) {
      return undefined;
    }

    const hasLeading = federatedRole !== federatedRole.trimStart();
    const hasTrailing = federatedRole !== federatedRole.trimEnd();

    if (hasLeading && hasTrailing) {
      return 'Remove the blank space before and after the role.';
    }
    if (hasLeading) {
      return 'Remove the blank space before the role.';
    }
    if (hasTrailing) {
      return 'Remove the blank space after the role.';
    }
    return undefined;
  })();

  // The full list is already on screen, so a duplicate is worth catching before
  // the round trip. The backend refuses it too.
  const isDuplicate = existingRoles.includes(trimmedRole);

  const canSubmit =
    trimmedRole.length > 0 &&
    supplierId.length > 0 &&
    !whitespaceReason &&
    !isDuplicate;

  const handleClose = () => {
    setFederatedRole('');
    setSupplierId('');
    setSupplierSearchTerm('');
    setChocoSupplierRole(SupplierUserRole.SALES_REP);
    setFailure(null);
    onClose();
  };

  const handleConfirm = async () => {
    setFailure(null);

    const result = await createRoleMapping({
      connectionId,
      federatedRole: trimmedRole,
      supplierId,
      chocoSupplierRole,
    });

    if (result.kind !== 'success') {
      setFailure(result);
      return;
    }

    addNotification({
      title: 'Role mapping added',
      message: `Anyone ${connectionName} sends with "${result.roleMapping.federatedRole}" now gets access to ${result.roleMapping.supplierName ?? 'the distributor'}.`,
      type: 'success',
    });
    handleClose();
  };

  // A validation failure names the field it is about, so it belongs against
  // that control. Anything else is about the request as a whole.
  const duplicateReason = isDuplicate
    ? 'This connection already maps that role.'
    : undefined;

  const roleReason =
    whitespaceReason ??
    duplicateReason ??
    (failure ? reasonForField(failure, 'federatedRole') : undefined);
  const supplierReason = failure
    ? reasonForField(failure, 'supplier.supplierId')
    : undefined;
  const bannerMessage =
    failure && failure.kind !== 'validation'
      ? failureMessage(failure)
      : undefined;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add role mapping"
      subtitle={`Give everyone who arrives from ${connectionName} with one role access to a distributor.`}
      footerContent={{
        onCancel: handleClose,
        onConfirm: handleConfirm,
        confirmText: 'Add role mapping',
        isLoading: isCreating,
        isDisabled: !canSubmit,
      }}
    >
      <div className="space-y-4">
        {bannerMessage && (
          <div className="rounded-lg border border-danger-200 bg-danger-50 p-3">
            <p className="text-sm text-danger-700">{bannerMessage}</p>
          </div>
        )}

        <Input
          label="Role sent by the provider"
          placeholder="app.choprd.halperns_admin"
          description="Type it exactly as the customer's directory sends it."
          value={federatedRole}
          onValueChange={setFederatedRole}
          isInvalid={!!roleReason}
          errorMessage={roleReason}
          variant="bordered"
        />

        <Autocomplete
          label="Distributor"
          placeholder="Search distributors by name"
          description={
            supplierReason ??
            (supplierSearchTerm.length > 0 &&
            supplierSearchTerm.length < SUPPLIER_SEARCH_MIN_CHARS
              ? SEARCH_HINT
              : 'People with this role get access to this distributor.')
          }
          variant="bordered"
          isLoading={isSearching}
          isInvalid={!!supplierReason}
          errorMessage={supplierReason}
          items={suppliers}
          selectedKey={supplierId || null}
          onInputChange={(value) => {
            setSupplierSearchTerm(value);
            searchSuppliers(value);
          }}
          onSelectionChange={(key) => setSupplierId(key ? String(key) : '')}
        >
          {(supplier) => (
            // textValue is set explicitly: with a composed child the input
            // would otherwise show the whole row once one is picked.
            <AutocompleteItem key={supplier.id} textValue={supplier.name}>
              <div className="flex flex-col">
                <span className="text-sm text-foreground">{supplier.name}</span>
                <span className="font-mono text-xs text-default-400">
                  Ref {supplier.referenceId} · {shortenId(supplier.id)}
                </span>
              </div>
            </AutocompleteItem>
          )}
        </Autocomplete>

        <div className="space-y-2">
          <p className="text-sm text-foreground">Role in Choco</p>
          <SupplierRoleSelect
            value={chocoSupplierRole}
            onChange={setChocoSupplierRole}
          />
        </div>
      </div>
    </BaseModal>
  );
};
